import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useGetFgaUserResourcesQuery, RootState } from '@circuitry-ai/doc-data';
import { CtyShowToast, checkType } from '../toast/CtyToast';

export function useCheckApplicationResource(applicationResourceId: string) {
  const { t } = useTranslation();
  const { details: user } = useSelector((state: RootState) => state.auth.user);
  const userId = user?.sub;
  const { refetch } = useGetFgaUserResourcesQuery(userId || '', { skip: !userId });
  const [isChecking, setIsChecking] = useState(false);

  const checkAndProceed = useCallback(
    async (onAllowed: () => void) => {

      setIsChecking(true);
      try {
        const result = await refetch();
        const appResources = (result.data?.data?.resources?.application_resources ?? []) as Array<{
          id?: string;
          name?: string;
          relation?: string;
        }>;
        const hasAccess = appResources.some(
          (r) =>
            (r.id?.toLowerCase() === applicationResourceId.toLowerCase() ||
              r.name?.toLowerCase() === applicationResourceId.toLowerCase()) &&
            r.relation === 'view'
        );
        if (hasAccess) {
          onAllowed();
        } else {
          CtyShowToast(checkType.error, t('You do not have access to {{resourceName}}.', { resourceName: applicationResourceId.split("_")[0] }));
        }
      } finally {
        setIsChecking(false);
      }
    },
    [applicationResourceId, refetch, t]
  );

  return { checkAndProceed, isChecking };
}

export function useHasApplicationResource(applicationResourceId: string): boolean {
  const { details: user } = useSelector((state: RootState) => state.auth.user);
  const userId = user?.sub;
  const { data } = useGetFgaUserResourcesQuery(userId || '', { skip: !userId });

  return useMemo(() => {
    const appResources = (data?.data?.resources?.application_resources ?? []) as Array<{
      id?: string;
      name?: string;
      relation?: string;
    }>;
    return appResources.some(
      (r) =>
        (r.id?.toLowerCase() === applicationResourceId.toLowerCase() ||
          r.name?.toLowerCase() === applicationResourceId.toLowerCase()) &&
        r.relation === 'view'
    );
  }, [data, applicationResourceId]);
}
